"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SSFarm = void 0;
const tslib_1 = require("tslib");
const solana_contrib_1 = require("@saberhq/solana-contrib");
const token_utils_1 = require("@saberhq/token-utils");
const spl_token_1 = require("@solana/spl-token");
const web3_js_1 = require("@solana/web3.js");
const mine_1 = require("../mine");
const pda_1 = require("./pda");
const vault_1 = require("./vault");
class SSFarm {
    constructor(sdk) {
        this.sdk = sdk;
    }
    get sbrAddress() {
        return this.sdk.environment.sbrAddress;
    }
    get programs() {
        return this.sdk.programs;
    }
    get program() {
        return this.programs.SunnySaberFarm;
    }
    get provider() {
        return this.sdk.provider;
    }
    /**
     * Creates a new pool.
     * @param param0
     * @returns
     */
    newPool({ creator = this.provider.wallet.publicKey, lpMint, payer = this.provider.wallet.publicKey, landlord = this.sdk.environment.landlord, farmMintKP = web3_js_1.Keypair.generate(), }) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const lpMintRaw = yield this.provider.connection.getAccountInfo(lpMint);
            if (!lpMintRaw) {
                throw new Error(`Could not find mint: ${lpMint.toString()}`);
            }
            const parsedLPMint = (0, token_utils_1.deserializeMint)(lpMintRaw.data);
            const program = this.programs.SunnySaberFarm;
            const [pool, bump] = yield (0, pda_1.findPoolAddress)({
                programId: program.programId,
                creator,
                lpMint,
                rewardsMint: this.sbrAddress,
                landlord,
            });
            const { accounts, instructions: createAccountInstructions } = yield (0, token_utils_1.getOrCreateATAs)({
                provider: this.provider,
                mints: {
                    sbr: this.sbrAddress,
                    lp: lpMint,
                },
                owner: pool,
            });
            const createFarmMint = yield (0, token_utils_1.createInitMintInstructions)({
                provider: this.provider,
                mintKP: farmMintKP,
                decimals: parsedLPMint.decimals,
                mintAuthority: pool,
                freezeAuthority: pool,
            });
            const newPoolIx = this.programs.SunnySaberFarm.instruction.newPool(bump, {
                accounts: {
                    creator,
                    landlord,
                    rewardsMint: this.sbrAddress,
                    pool,
                    lpMint,
                    payer,
                    systemProgram: web3_js_1.SystemProgram.programId,
                    rewardsFeeAccount: accounts.sbr,
                    lpFeeAccount: accounts.lp,
                    farmMint: farmMintKP.publicKey,
                },
            });
            const createPool = new solana_contrib_1.TransactionEnvelope(this.provider, [
                ...createAccountInstructions,
                newPoolIx,
            ]);
            return {
                key: pool,
                tx: createFarmMint.combine(createPool),
            };
        });
    }
    updateClaimFee({ pool, fee, admin = this.provider.wallet.publicKey, }) {
        const megaBPS = new token_utils_1.u64(fee.asFraction
            // convert [0,1] fraciton into bps
            .multiply(10000)
            // convert bps into megabps
            .multiply(1000000)
            .quotient.toString());
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            this.program.instruction.updateClaimFee({ megaBps: megaBPS }, {
                accounts: {
                    admin,
                    pool,
                },
            }),
        ]);
    }
    fetchPoolData(key) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            return {
                key,
                data: yield this.programs.SunnySaberFarm.account.pool.fetch(key),
            };
        });
    }
    fetchVaultData(key) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            return {
                key,
                data: yield this.programs.SunnySaberFarm.account.vault.fetch(key),
            };
        });
    }
    findPoolAddress({ creator = this.sdk.environment.creator, lpMint, landlord = this.sdk.environment.landlord, }) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const program = this.programs.SunnySaberFarm;
            const [pool] = yield (0, pda_1.findPoolAddress)({
                programId: program.programId,
                creator,
                lpMint,
                rewardsMint: this.sbrAddress,
                landlord,
            });
            return pool;
        });
    }
    findVaultAddress({ owner = this.provider.wallet.publicKey, pool, }) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const program = this.programs.SunnySaberFarm;
            const [vault] = yield (0, pda_1.findVaultAddress)({
                programId: program.programId,
                pool,
                owner,
            });
            return vault;
        });
    }
    /**
     * Creates a new Vault.
     * @param param0
     * @returns
     */
    newVault({ owner = this.provider.wallet.publicKey, payer = this.provider.wallet.publicKey, pool: { key: poolKey, data: { landlord = this.sdk.environment.landlord, lpMint, farmMint }, }, }) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const program = this.programs.SunnySaberFarm;
            const [vault, bump] = yield (0, pda_1.findVaultAddress)({
                programId: program.programId,
                pool: poolKey,
                owner,
            });
            // vault ATAs
            const { accounts, instructions } = yield (0, token_utils_1.getOrCreateATAs)({
                provider: this.provider,
                mints: {
                    sbr: this.sbrAddress,
                    lp: lpMint,
                    farm: farmMint,
                },
                owner: vault,
            });
            const plot = yield this.programs.Farm.account.plot.associatedAddress(landlord, lpMint);
            const farmer = yield this.programs.Farm.account.farmer.associatedAddress(vault, plot);
            const [quarry] = yield (0, mine_1.findQuarryAddress)(this.sdk.environment.rewarder, farmMint);
            const [miner, minerBump] = yield (0, mine_1.findMinerAddress)(quarry, vault);
            const farmerATA = yield (0, token_utils_1.getOrCreateATA)({
                provider: this.provider,
                mint: lpMint,
                owner: farmer,
            });
            const minerATA = yield (0, token_utils_1.getOrCreateATA)({
                provider: this.provider,
                mint: farmMint,
                owner: miner,
            });
            const newVaultIX = this.programs.SunnySaberFarm.instruction.newVault(bump, {
                accounts: {
                    pool: poolKey,
                    owner,
                    vault,
                    lpTokenAccount: accounts.lp,
                    rewardsAccount: accounts.sbr,
                    payer,
                    rent: web3_js_1.SYSVAR_RENT_PUBKEY,
                    systemProgram: web3_js_1.SystemProgram.programId,
                    farmProgram: this.programs.Farm.programId,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    associatedTokenProgram: spl_token_1.ASSOCIATED_TOKEN_PROGRAM_ID,
                    lpTokenMint: lpMint,
                    farmer,
                    farmerVault: farmerATA.address,
                    plot,
                },
            });
            const newMinerIX = this.programs.SunnySaberFarm.instruction.initMiner(minerBump, {
                accounts: {
                    vault,
                    systemProgram: web3_js_1.SystemProgram.programId,
                    payer,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    mineProgram: this.programs.Mine.programId,
                    miner,
                    quarry,
                    farmMint,
                    minerVault: minerATA.address,
                },
            });
            return {
                key: vault,
                tx: new solana_contrib_1.TransactionEnvelope(this.provider, [
                    ...(farmerATA.instruction ? [farmerATA.instruction] : []),
                    ...(minerATA.instruction ? [minerATA.instruction] : []),
                    ...instructions,
                    newVaultIX,
                    newMinerIX,
                ]),
            };
        });
    }
    /**
     * Loads a vault.
     * @returns
     */
    loadVault({ vaultKey }) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const vault = yield this.fetchVaultData(vaultKey);
            const pool = yield this.fetchPoolData(vault.data.pool);
            return new vault_1.Vault(this, pool, vault);
        });
    }
}
exports.SSFarm = SSFarm;
//# sourceMappingURL=sunnySaberFarm.js.map